interface AreaChartProps {
  series: ReadonlyArray<ReadonlyArray<number>>;
  days: number;
}

export function AreaChart({ series, days }: AreaChartProps) {
  const width = 800;
  const height = 200;
  const pad = { top: 12, right: 12, bottom: 28, left: 36 };

  const all = series.flat();
  const max = Math.max(...all, 1);
  const min = Math.min(...all);

  const stepX = (width - pad.left - pad.right) / Math.max(1, series[0].length - 1);

  const coordsFor = (pts: ReadonlyArray<number>) =>
    pts.map((p, i) => {
      const x = pad.left + i * stepX;
      const y =
        pad.top + (height - pad.top - pad.bottom) * (1 - (p - min) / Math.max(1, max - min));
      return [x, y] as const;
    });

  const linePath = (pts: ReadonlyArray<number>) =>
    coordsFor(pts)
      .map(([x, y], i) => (i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`))
      .join(' ');

  const areaPath = (pts: ReadonlyArray<number>) => {
    const line = linePath(pts);
    const lastX = pad.left + (pts.length - 1) * stepX;
    return `${line} L${lastX.toFixed(1)},${height - pad.bottom} L${pad.left},${height - pad.bottom} Z`;
  };

  const yTicks = 4;
  const yValues = Array.from({ length: yTicks + 1 }, (_, i) =>
    Math.round(min + ((max - min) * (yTicks - i)) / yTicks),
  );

  const xLabelCount = Math.min(6, days);
  const xIdxs = Array.from({ length: xLabelCount }, (_, i) =>
    Math.round((i * (series[0].length - 1)) / (xLabelCount - 1)),
  );

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-[260px] w-full"
      preserveAspectRatio="none"
      aria-label="Traffic over time"
    >
      <defs>
        <linearGradient id="area-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#0F172A" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#0F172A" stopOpacity="0" />
        </linearGradient>
      </defs>

      {yValues.map((v, i) => {
        const y = pad.top + ((height - pad.top - pad.bottom) * i) / yTicks;
        return (
          <g key={i}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={y}
              y2={y}
              stroke="#E2E8F0"
              strokeWidth={1}
              strokeDasharray={i === 0 ? undefined : '2 4'}
            />
            <text
              x={pad.left - 8}
              y={y + 3}
              textAnchor="end"
              className="fill-ink-faint font-mono"
              fontSize={10}
            >
              {v.toLocaleString()}
            </text>
          </g>
        );
      })}

      {xIdxs.map((i) => {
        const x = pad.left + i * stepX;
        return (
          <text
            key={i}
            x={x}
            y={height - 8}
            textAnchor="middle"
            className="fill-ink-faint font-mono"
            fontSize={10}
          >
            {days - i}d ago
          </text>
        );
      })}

      <path d={areaPath(series[0])} fill="url(#area-grad)" />
      <path d={linePath(series[0])} fill="none" stroke="#0F172A" strokeWidth={1.75} />

      {series[1] ? (
        <path
          d={linePath(series[1])}
          fill="none"
          stroke="#0F172A"
          strokeWidth={1.25}
          strokeDasharray="4 3"
        />
      ) : null}
    </svg>
  );
}
